/**
 * Schema-driven form fields, ported from portal-ui-lib's `form-modal/types`. Field types the
 * dealer features need are supported; `tags` and the uploading `image` mode (addons,
 * organization — phase C) are not yet (docs/dealer-portal-react/ui.md). `image` is the deferred
 * mode only: the form holds the picked file, the caller uploads it after saving.
 */

export type FormFieldType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'tel'
  | 'textarea'
  | 'select'
  | 'checkbox'
  | 'radio'
  | 'checklist'
  | 'repeater'
  | 'image'
  | 'notice';

/**
 * An `image` field's value (Angular's `ModalFormDeferredImageValue`). Start it from the stored
 * image's URL (or null); `initialFormValue` turns that into this shape.
 */
export interface ImageFieldValue {
  /** What to show: a picked file's data URL, the stored image's URL, or null. */
  preview: string | null;
  /** A newly picked file, base64 without the `data:` prefix; null when unchanged. */
  base64: string | null;
  contentType: string | null;
  /** The stored image was removed and nothing picked instead. */
  removeImage: boolean;
}

export const EMPTY_IMAGE: ImageFieldValue = {
  preview: null,
  base64: null,
  contentType: null,
  removeImage: false,
};

export interface FormFieldOption {
  value: string;
  label: string;
  description?: string;
  /** Shown beside the label and searchable (e.g. a model code). */
  badge?: string;
  /** Tree-mode indent. */
  indentLevel?: number;
  /** Tree mode: this option's parent; selecting a parent selects its subtree. */
  parentValue?: string;
  /** Grouped mode: headings above this option, outermost first. */
  groupPath?: string[];
  /** Grouped mode: a tag per heading level. */
  groupBadges?: (string | undefined)[];
  /** Other records this row stands for; selected together. */
  mergedValues?: string[];
}

export interface FormFieldCondition {
  field: string;
  eq?: unknown;
  neq?: unknown;
  in?: unknown[];
  notIn?: unknown[];
}

export interface RepeaterItemField {
  key: string;
  type: 'text' | 'number';
  label: string;
  required?: boolean;
  min?: number;
  max?: number;
  placeholder?: string;
  defaultValue?: string | number | null;
}

export interface FormFieldSchema {
  key: string;
  type: FormFieldType;
  label: string;
  required?: boolean;
  placeholder?: string;
  options?: FormFieldOption[];
  /** Checklist: a search box above the options. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Repeater: the fields of each row. */
  itemFields?: RepeaterItemField[];
  addButtonLabel?: string;
  emptyLabel?: string;
  /** Notice: the text shown. */
  content?: string;
  noticeVariant?: 'default' | 'info' | 'danger' | 'warning';
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: RegExp | string;
  /** Only in these modes (default: both). */
  visibleInModes?: ('create' | 'edit')[];
  /** Read-only when editing. */
  disabledWhenEditing?: boolean;
  /** Shown only while every condition holds. */
  visibleWhen?: FormFieldCondition[];
  /** Spans both grid columns. */
  fullWidth?: boolean;
  /** Image: the button labels, worded by the feature. */
  imageLabels?: { select: string; replace: string; remove: string };
  /** Image: the file types offered (default `image/*`). */
  accept?: string;
}

export interface FormSchema {
  fields: FormFieldSchema[];
  /** Titled groups of fields, by key; fields not listed render first, untitled. */
  sections?: { sectionTitle: string; fieldKeys: string[] }[];
}

export type FormValue = Record<string, unknown>;
export type FormMode = 'create' | 'edit';
